/**
 * A wizard state read off a floor plan: what the estimator's results page
 * needs beside the plan's own materials (the config strips, the visualizer
 * payload, `buildEstimateInput`), when the estimate comes from the 3D
 * designer rather than the wizard. The inverse, as far as there is one, of
 * `designerDocumentFromWizard`.
 *
 *   - dimensions: the focus room's;
 *   - wall colour and board direction: the majority, by area, of the room's
 *     Wall&CeilingBoard sections (the base's values when it has none);
 *   - SlatWall: the majority colour of its SlatWall sections, rows from the
 *     tallest one (6" a row), the start height from the lowest;
 *   - ceiling: included when the ceiling (or any plane of it) has a section,
 *     in the majority colour;
 *   - space: the plan's; garage door(s) from the garageDoor openings — how
 *     many, the largest size, and which wall (0/2 the length walls, 1/3 the
 *     width walls).
 */

import { PanelOrientation, ProductColor, WizardState } from '../../types';
import { Section, Surface, roomSurfaceList } from '../../types/sections';
import { FloorPlan, findRoom, roomDims } from './floorPlan';

export type WizardSeed = Pick<WizardState, 'calculationMode' | 'wallsConfig' | 'ceilingConfig' | 'dimensionsConfig' | 'applicationConfig' | 'individualWalls'>;

const SLATWALL_ROW_FT = 0.5;

/** The value with the most area behind it, or `fallback` when there are no sections. */
function majority<T extends string>(sections: Section[], pick: (s: Section) => T | undefined, fallback: T): T {
  const area = new Map<T, number>();
  for (const s of sections) {
    const v = pick(s);
    if (v === undefined) continue;
    area.set(v, (area.get(v) ?? 0) + s.width * s.height);
  }
  let best: T = fallback;
  let most = 0;
  area.forEach((a, v) => { if (a > most) { most = a; best = v; } });
  return best;
}

export function wizardSeedFromPlan(plan: FloorPlan, focusRoomId: string | null, base: WizardSeed): WizardSeed {
  const room = findRoom(plan, focusRoomId) ?? plan.rooms[0];
  if (!room) return { ...base, calculationMode: 'FullRoom', individualWalls: undefined };
  const walls = room.surfaces.walls;
  const wallSections = walls.flatMap((w) => w.sections);
  const wcb = wallSections.filter((s) => s.productType === 'WCB');
  const slat = wallSections.filter((s) => s.productType === 'SlatWall');
  const ceilingSurfaces: Surface[] = roomSurfaceList(room.surfaces).filter((s) => s.kind === 'ceiling');
  const ceilingSections = ceilingSurfaces.flatMap((s) => s.sections);

  const rows = slat.length ? Math.max(1, Math.round(Math.max(...slat.map((s) => s.height)) / SLATWALL_ROW_FT)) : 0;
  const startHeight = slat.length ? Math.min(...slat.map((s) => s.y)) : base.wallsConfig.slatWallStartHeight;

  // Garage doors: the openings of that kind, on whichever wall has them.
  let doors = 0, doorWidth = 0, doorHeight = 0, doorWall = -1;
  walls.forEach((w, i) => {
    for (const o of w.openings ?? []) {
      if (o.kind !== 'garageDoor') continue;
      doors++;
      doorWidth = Math.max(doorWidth, o.width);
      doorHeight = Math.max(doorHeight, o.height);
      if (doorWall < 0) doorWall = i;
    }
  });

  return {
    calculationMode: 'FullRoom',
    individualWalls: undefined,
    dimensionsConfig: { usePreset: false, presetId: undefined, customDimensions: roomDims(room) },
    wallsConfig: {
      ...base.wallsConfig,
      color: majority<ProductColor>(wcb, (s) => s.color, base.wallsConfig.color),
      orientation: majority<PanelOrientation>(wcb, (s) => s.orientation, base.wallsConfig.orientation),
      slatWallColor: majority<ProductColor>(slat, (s) => s.color, base.wallsConfig.slatWallColor),
      slatWallNumPanels: rows,
      slatWallStartHeight: startHeight,
      slatWallEnabled: rows > 0,
      slatWallCoveragePercent: 0,
      usePresets: false,
      selectedPresetId: null,
    },
    ceilingConfig: {
      ...base.ceilingConfig,
      include: ceilingSections.length > 0,
      color: majority<ProductColor>(ceilingSections, (s) => s.color, base.ceilingConfig.color),
      zones: undefined,
    },
    applicationConfig: {
      ...base.applicationConfig,
      spaceType: plan.spaceType ?? base.applicationConfig.spaceType,
      garage: {
        enabled: doors > 0,
        numDoors: doors,
        width: doorWidth,
        height: doorHeight,
        onWallType: doors === 0 ? 'None' : doorWall % 2 === 0 ? 'LengthWall' : 'WidthWall',
      },
    },
  };
}
